import React, { useState, useEffect } from 'react';

/**
 * Settings — overlay panel for configuring HAL Voice.
 *
 * Props:
 *   settings — current settings object
 *   onSave   — called with new settings object
 *   onClose  — called to dismiss the panel
 */
export default function Settings({ settings, onSave, onClose }) {
  const [local, setLocal] = useState({ ...settings });
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesError, setVoicesError] = useState('');

  const update = (key, value) => setLocal((prev) => ({ ...prev, [key]: value }));

  // Fetch available voices from Kokoro TTS server
  useEffect(() => {
    if (!local.ttsUrl) return;
    setVoicesLoading(true);
    setVoicesError('');

    fetch(local.ttsUrl + '/voices')
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then((data) => {
        // Kokoro returns an object like { voices: ["af_heart", ...] } or just an array
        const voiceList = Array.isArray(data) ? data : (data.voices || Object.keys(data));
        setVoices(voiceList);
        setVoicesLoading(false);
      })
      .catch((err) => {
        console.warn('[Settings] Could not fetch voices:', err);
        setVoicesError('Could not reach TTS server');
        setVoicesLoading(false);
      });
  }, [local.ttsUrl]);

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <h2>Settings</h2>

        <div className="setting-group">
          <label>Worker URL</label>
          <input
            type="text"
            value={local.workerUrl}
            onChange={(e) => update('workerUrl', e.target.value)}
            placeholder="https://hal-voice.dave-d42.workers.dev"
          />
        </div>

        <div className="setting-group">
          <label>TTS Server URL</label>
          <input
            type="text"
            value={local.ttsUrl || ''}
            onChange={(e) => update('ttsUrl', e.target.value)}
            placeholder="http://192.168.6.128:8484"
          />
          <small style={{ color: 'var(--text-secondary, #888)', fontSize: '0.75rem' }}>
            Kokoro TTS on M2 LAN. Must be reachable from this device.
          </small>
        </div>

        <div className="setting-group">
          <label>Voice</label>
          {voices.length > 0 ? (
            <select
              value={local.voiceId || ''}
              onChange={(e) => update('voiceId', e.target.value)}
            >
              <option value="">Default</option>
              {voices.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={local.voiceId || ''}
              onChange={(e) => update('voiceId', e.target.value)}
              placeholder="af_heart"
            />
          )}
          {voicesLoading && (
            <small style={{ color: 'var(--text-secondary, #888)', fontSize: '0.75rem' }}>
              Loading voices...
            </small>
          )}
          {voicesError && (
            <small style={{ color: '#f44', fontSize: '0.75rem' }}>
              {voicesError}
            </small>
          )}
        </div>

        <div className="setting-group">
          <label>System Prompt</label>
          <textarea
            value={local.systemPrompt}
            onChange={(e) => update('systemPrompt', e.target.value)}
            rows={4}
          />
        </div>

        <div className="setting-group">
          <div className="toggle-row">
            <label style={{ marginBottom: 0 }}>Push-to-Talk Mode</label>
            <button
              className={`toggle ${local.pushToTalk ? 'active' : ''}`}
              onClick={() => update('pushToTalk', !local.pushToTalk)}
              aria-label="Toggle push-to-talk"
            />
          </div>
        </div>

        <div className="settings-actions">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-save" onClick={() => onSave(local)}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
